import type { PropertyFilterProps } from '@risk-smart/themed-cloudscape-components/property-filter';
import PropertyFilter from '@risk-smart/themed-cloudscape-components/property-filter';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import styles from './style.module.scss';

// Cloudscape's built-in autosuggest filter (see node_modules
// @cloudscape-design/components/property-filter/filter-options.js
// `matchSingleOption`) matches the typed search text against
// `label || value`. For dropdowns like Owner and Tags the `value`
// is a UUID, so typing a digit or short string matches the hex/digit
// content of the UUID — producing a list of unrelated names/tags
// (RSP-5284). Pre-filter the options by label BEFORE handing them to
// Cloudscape so the UUID value can no longer contaminate the match.
//
// Edge case (RSP-5530): when an existing filter token is opened for
// editing, Cloudscape's token editor (token-editor-inputs.js
// `ValueInputAuto`) fires `onLoadItems` with `filteringText` set to
// the existing token's UUID and looks up the matching option in the
// supplied `filteringOptions` to resolve the human-readable label
// (`matchedOption?.label ?? value`). Naïve label-only narrowing drops
// that option, the editor falls back to rendering the raw UUID, and
// the polluted filtering text leaves the value dropdown empty when
// the user starts typing over it. Preserve the option whose value
// exactly matches the current filtering text so the editor can still
// resolve its label without weakening the label-only narrowing for
// genuine typed searches (the UUID is never a typed search itself —
// the user types names, not 36-character hex strings).
const PropertyFilterPanel: FC<PropertyFilterProps> = ({
  filteringOptions,
  onLoadItems,
  ...rest
}) => {
  const [filteringText, setFilteringText] = useState('');
  const [filteringPropertyKey, setFilteringPropertyKey] = useState<
    string | undefined
  >(undefined);

  const visibleOptions = useMemo(() => {
    if (!filteringOptions || !filteringText || !filteringPropertyKey) {
      return filteringOptions;
    }
    const needle = filteringText.toLowerCase();

    // When a filter token is opened for editing, Cloudscape's token editor echoes
    // the token's raw value (e.g. a department UUID) back through `onLoadItems` as
    // the filtering text, and does NOT re-fire `onLoadItems` when the value field
    // is subsequently cleared. That raw value never appears in any option's label,
    // so the label-based narrowing below would collapse the list to the single
    // value-matching option and leave it collapsed — the full option list "doesn't
    // appear straight away" when editing a token, only reappearing once another
    // `onLoadItems` fires (typing, or clicking off and reopening the editor)
    // (RSP-6036). Detect that echo and show the full list instead; the matching
    // option stays present, so the editor still resolves its human label (RSP-5530).
    //
    // The echo is the filtering text exactly matching a labelled option's value
    // AND not itself being findable within that label. The label check keeps this
    // from swallowing a genuine short search: typing "open" for an option labelled
    // "Open" is a substring of its own label, so it is treated as a search and
    // narrowed normally rather than mistaken for a token echo (an opaque value like
    // a UUID never appears in its label).
    const isTokenValueEcho = filteringOptions.some(
      (option) =>
        option.propertyKey === filteringPropertyKey &&
        option.value === filteringText &&
        option.label != null &&
        !option.label.toLowerCase().includes(needle)
    );
    if (isTokenValueEcho) {
      return filteringOptions;
    }

    return filteringOptions.filter((option) => {
      if (option.propertyKey !== filteringPropertyKey) {
        return true;
      }
      // Match on label (keeps UUID values out of the match, RSP-5284); fall
      // back to value for auto-derived options that have no label (RSP-5769).
      const haystack = (option.label ?? option.value ?? '').toLowerCase();

      return haystack.includes(needle);
    });
  }, [filteringOptions, filteringText, filteringPropertyKey]);

  return (
    <div className={styles.propertyFilter}>
      <PropertyFilter
        {...rest}
        filteringOptions={visibleOptions}
        onLoadItems={(event) => {
          setFilteringText(event.detail.filteringText ?? '');
          setFilteringPropertyKey(event.detail.filteringProperty?.key);
          onLoadItems?.(event);
        }}
      />
    </div>
  );
};

export default PropertyFilterPanel;
